<div class="grid grid-cols-1 lg:grid-cols-3 gap-5">
  <!-- Intro / steps -->
  <div
    class="lg:col-span-1 rounded-2xl border !border-slate-200/70 bg-white p-3 sm:p-5 flex flex-col"
  >
    <div
      class="inline-flex items-center gap-1.5 text-[12px] font-semibold uppercase tracking-[0.12em] text-[var(--primary-color)]"
    >
      <span class="w-1.5 h-1.5 rounded-full bg-[var(--primary-color)]"></span>
      {{ content?.eyebrow }}
    </div>

    <h2 class="text-[22px] leading-tight font-semibold text-slate-900 mt-2">
      {{ content?.heading }}
    </h2>
    <p class="text-[14px] text-slate-500 mt-2">
      {{ content?.introText }}
    </p>

    <div class="mt-5 flex flex-col gap-3.5">
      @for (step of steps; track step.title) {
        <div class="flex items-start gap-3">
          <span
            class="shrink-0 w-9 h-9 rounded-lg flex items-center justify-center bg-[color-mix(in_srgb,var(--primary-color)_10%,white)] text-[var(--primary-color)]"
          >
            <igntr-icon [name]="step.icon" [size]="18"></igntr-icon>
          </span>
          <div>
            <div class="text-[14px] font-semibold text-slate-900">
              {{ step.title }}
            </div>
            <div class="text-[12px] text-slate-500">{{ step.sub }}</div>
          </div>
        </div>
      }
    </div>

    <div
      class="mt-auto pt-4 flex items-center gap-1.5 text-[12px] text-slate-400"
    >
      <igntr-icon name="calendar" [size]="14"></igntr-icon>
      {{ content?.duration }}
    </div>
  </div>

  <!-- Questionnaire -->
  <div
    class="lg:col-span-2 rounded-2xl border !border-slate-200/70 bg-white p-3 sm:p-5 flex flex-col"
  >
    <div class="flex items-start justify-between gap-4">
      <div>
        <h3 class="text-[16px] font-semibold text-slate-900">
          {{ content?.cardTitle }}
        </h3>
        <p class="text-[13px] text-slate-500 mt-0.5">
          {{ content?.cardSubtitle }}
        </p>
      </div>
      <span class="shrink-0 text-[13px] text-slate-400 whitespace-nowrap">
        {{ counterLabel }}
      </span>
    </div>

    <div class="mt-5 flex items-start gap-3">
      <span
        class="shrink-0 w-6 h-6 rounded-md bg-[var(--primary-color)] text-white text-[13px] font-semibold flex items-center justify-center"
      >
        {{ current.number }}
      </span>
      <div>
        <h4 class="text-[15px] font-semibold text-slate-900 leading-snug">
          {{ current.title }}
        </h4>
        <p class="text-[13px] text-slate-500 mt-0.5">{{ current.hint }}</p>
        @if (current.note) {
          <p class="text-[12px] text-slate-400 mt-1">{{ current.note }}</p>
        }
      </div>
    </div>

    <div class="mt-4 flex flex-col gap-2">
      @for (option of current.options; track option.id; let i = $index) {
        <button
          type="button"
          (click)="toggle(option.id)"
          class="w-full text-left flex items-center gap-3 px-3.5 py-3 rounded-xl border transition-colors"
          [ngClass]="
            isSelected(option.id)
              ? '!border-[var(--primary-color)] bg-[color-mix(in_srgb,var(--primary-color)_7%,white)]'
              : 'border-slate-200 hover:!border-slate-300 hover:bg-slate-50/60'
          "
        >
          <span
            class="shrink-0 w-6 h-6 rounded-md flex items-center justify-center text-[12px] font-semibold transition-colors"
            [ngClass]="
              isSelected(option.id)
                ? 'bg-[var(--primary-color)] text-white'
                : 'bg-slate-100 text-slate-500'
            "
          >
            @if (isSelected(option.id)) {
              <igntr-icon name="check" [size]="14"></igntr-icon>
            } @else {
              {{ letters[i] }}
            }
          </span>
          <span class="text-[14px] text-slate-700">{{ option.label }}</span>
        </button>
      }
    </div>

    @if (current.hasFreeText) {
      <div class="mt-4">
        <label class="block text-[13px] font-medium text-slate-600 mb-1.5">
          {{ current.freeTextLabel }}
        </label>
        <textarea
          [(ngModel)]="freeText"
          rows="2"
          [placeholder]="current.freeTextPlaceholder ?? ''"
          class="w-full px-3.5 py-2.5 rounded-xl bg-slate-50/50 focus:bg-white border !border-slate-200 focus:!border-[var(--primary-color)] text-[14px] text-slate-800 placeholder:text-slate-400 outline-none transition-colors resize-none"
        ></textarea>
      </div>
    }

    <div
      class="mt-2 pt-2 flex flex-col items-center gap-3 sm:mt-5 sm:pt-4 sm:flex-row sm:items-center sm:justify-between sm:gap-4"
    >
      <div class="flex items-center gap-1.5">
        @for (q of questions; track q.number; let i = $index) {
          <span
            class="h-1.5 rounded-full transition-all"
            [ngClass]="
              i === currentIndex
                ? 'w-6 bg-[var(--primary-color)]'
                : i < currentIndex
                  ? 'w-1.5 bg-[color-mix(in_srgb,var(--primary-color)_45%,white)]'
                  : 'w-1.5 bg-slate-200'
            "
          ></span>
        }
      </div>

      <div class="flex items-center gap-2">
        @if (!isFirst) {
          <button
            type="button"
            (click)="back()"
            class="inline-flex items-center h-9 px-4 rounded-lg border !border-slate-200 bg-white text-slate-600 text-[14px] font-medium hover:!bg-slate-50 transition-colors"
          >
            {{ content?.backLabel }}
          </button>
        }
        <button
          type="button"
          (click)="next()"
          [disabled]="!canProceed || (isLast && submitting)"
          class="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-[var(--primary-color)] text-white text-[14px] font-medium shadow-[0_8px_18px_-10px_rgba(15,23,42,.5)] transition disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {{ isLast ? content?.finishLabel : content?.nextLabel }}
          <igntr-icon name="chevronR" [size]="14"></igntr-icon>
        </button>
      </div>
    </div>
  </div>
</div>
